import React, { FC, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Case, Switch } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { CaptionMD, LabelMD, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { saveGuildContinueWatching } from '@/services/GuildContinueWatching'
import { Episode, Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isATheatricalSneakPeek } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface SneakPeekCTAProps {
  episode: Episode
  project: Project | undefined
}

export const SneakPeekCTA: FC<SneakPeekCTAProps> = React.memo(({ episode, project }) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const { playing, playingProgress } = useAngelPlayerContext()
  const [hasSavedGuildContinueWatching, setHasSavedGuildContinueWatching] = useState(false)

  const isTheatrical = isATheatricalSneakPeek(episode)

  const handleClick = () => {
    track('Clicked Sneak Peek CTA Button', {
      guid: episode?.guid,
      episode,
      project,
      isTheatrical,
    })
  }

  const renderContainerTime = useMemo(() => {
    if (episode.source.credits) {
      return Math.min(episode.source.credits - 25, episode.source.duration - 60)
    } else {
      return episode.source.duration - 60
    }
  }, [episode])

  const renderCtaTime = useMemo(() => {
    return renderContainerTime + 30
  }, [renderContainerTime])

  if (!project) return null

  const variants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        bounce: 0.2,
        duration: 0.6,
      },
    },
    closed: { y: 40, opacity: 0, display: 'none' },
  }

  const movieTitle = project.name
  const shouldRenderContainer = playingProgress > renderContainerTime
  const shouldRenderCta = playing && playingProgress > renderCtaTime
  const shouldSaveToContinueWatching = shouldRenderContainer && !hasSavedGuildContinueWatching && !isTheatrical

  if (shouldSaveToContinueWatching) {
    setHasSavedGuildContinueWatching(true)
    saveGuildContinueWatching(episode, track).then((result) => setHasSavedGuildContinueWatching(result))
  }

  if (!shouldRenderContainer) return null

  return (
    <div
      id="guild-player-button"
      className="absolute left-1/2 top-1/4 z-40 w-[98%] min-w-[350px] max-w-[480px] -translate-x-1/2 -translate-y-1/4 text-[10px] sm:text-base md:top-1/2 md:-translate-y-1/2"
    >
      <motion.div animate={shouldRenderCta ? 'open' : 'closed'} variants={variants}>
        <div
          className="rounded-2xl p-1"
          style={{
            background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
          }}
        >
          <div className="flex flex-row gap-2 rounded-xl bg-core-gray-900 p-2 md:gap-4 md:p-4">
            <div className="shrink-0">
              <Image
                src={getCloudinaryImageUrl({
                  path: project.discoveryPosterCloudinaryPath + '.webp',
                  width: 83,
                  height: 148,
                  transforms: 'c_fill',
                })}
                alt={`${movieTitle} poster`}
                width={83}
                height={148}
                className="rounded-xl"
              />
            </div>

            <div className="flex flex-col justify-between">
              <TitleXS weight="semibold">
                <Translate i18nKey="sneakPeakCTATitle" t={t}>
                  Want to Keep Watching?
                </Translate>
              </TitleXS>
              <CaptionMD>
                <Switch>
                  <Case condition={isTheatrical}>
                    <Translate i18nKey="sneakPeakTheatricalCTAMessage" t={t} values={{ movieTitle }}>
                      Buy tickets today to watch {{ movieTitle }} in theaters!
                    </Translate>
                  </Case>
                  <Case condition={!isTheatrical}>
                    <Translate i18nKey="sneakPeakCTAMessage" t={t} values={{ movieTitle }}>
                      Join the Angel Guild to finish {{ movieTitle }}, access our full library, and help amplify light.
                    </Translate>
                  </Case>
                </Switch>
              </CaptionMD>
              <LinkButton
                onClick={handleClick}
                variant="white"
                external
                target="_blank"
                href={isTheatrical ? `${paths.tickets.index}/${project.slug}` : `${paths.guild.join}/${project.slug}`}
                className="text-core-gray-950"
              >
                <div className="flex min-h-6 flex-row items-center">
                  <Switch>
                    <Case condition={isTheatrical}>
                      <TicketIcon color="black" size={20} />
                      <LabelMD className="pl-1 pt-[1px]">{t('getShowtimes', 'Get Showtimes')}</LabelMD>
                    </Case>
                    <Case condition={!isTheatrical}>
                      <GuildIconMono color="black" size={20} />
                      <LabelMD className="pl-1">{t('becomeAMember', 'Join to Continue')}</LabelMD>
                    </Case>
                  </Switch>
                </div>
              </LinkButton>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
})

SneakPeekCTA.displayName = 'React.memo(SneakPeakCTA)'
